import { ImportType, SqlImportDatabase } from 'cio/utils/sql-import';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { belongsTo, hasMany } from 'ember-data/relationships';
import MomentService from 'cio/services/moment';
import SegmentModel from 'cio/models/segment';
import SqlImportDatabaseModel from 'cio/models/sql-import-database';
import { modelApiAction } from 'cio/utils/model-api-actions';
import { IdentifierKey, ObjectIdentifierKey } from 'cio/utils/identifiers';
import SqlImportRowModel from 'cio/models/sql-import-row';
import pluralize from 'cio/utils/pluralize';
import { findRoundUnit, roundTimeframe } from 'cio/utils/round-timeframe-units';
import ObjectTypeModel from 'cio/models/object-type';

export enum ImportStatus {
  Draft = 'draft',
  Published = 'published',
}

export enum SyncTarget {
  People = 'People',
  Objects = 'Objects',
}

interface Relationship {
  query: string;
  entity1_type_id: number;
  entity1_id_field_name: string;
  entity2_type_id: number;
  entity2_id_field_name: string;
}

export type SQLImportIdFieldName = IdentifierKey.Id | IdentifierKey.Email | ObjectIdentifierKey.ObjectId;

export default class SqlImportModel extends Model {
  @service moment!: MomentService;
  @service store!: DS.Store;

  @attr('string') name!: string;
  @attr('string') description!: string;
  @attr('number') interval!: number;
  @attr('string', { defaultValue: ImportType.Workspace }) importType!: ImportType;
  @attr('string') idFieldName!: SQLImportIdFieldName;
  @attr('string') segmentName!: string;
  @attr('string') query!: string;
  @attr('string') location!: string;

  @attr('number') createdAt!: number;
  @attr('number') updatedAt!: number;
  @attr('boolean') active!: boolean;
  @attr('number', { defaultValue: 0 }) startAt!: number;
  @attr('number') activatedAt!: number;
  @attr('number') lastSyncedAt!: number;
  @attr('string') databaseType!: SqlImportDatabase;
  @attr('boolean', { defaultValue: true }) isDraft!: boolean;
  @attr('string', { defaultValue: ImportStatus.Draft }) ImportStatus!: ImportStatus;
  @attr('boolean', { defaultValue: false }) clearEmptyCells!: boolean;
  @attr('boolean', { defaultValue: true }) ignoreSuppressErr!: boolean;

  @attr({ defaultValue: null }) relationship!: Relationship | null;

  @belongsTo('segment', { async: true, inverse: null }) segment!: DS.PromiseObject<SegmentModel>;
  @belongsTo('sql-import-database', { async: false, inverse: null }) database?: SqlImportDatabaseModel;
  @hasMany('sql-import-row', { async: false, inverse: 'import' }) sqlImportRows!: DS.ManyArray<SqlImportRowModel[]>;
  @belongsTo('object-type', { async: false, inverse: null }) objectType!: ObjectTypeModel;

  @readOnly('sqlImportRows') imports!: SqlImportRowModel[];

  @computed('importType')
  get isImportingToSegment() {
    return this.importType === ImportType.WorkspaceAndSegment;
  }

  @computed('startAt', 'moment.currentTimestamp')
  get isScheduled() {
    return this.startAt && this.startAt > this.moment.currentTimestamp;
  }

  @computed('interval')
  get duration() {
    let delay = roundTimeframe(this.interval);
    let { value, seconds } = findRoundUnit(delay);
    let count = delay / seconds;
    return `${count} ${pluralize(count, value)}`;
  }

  get objectTypeName() {
    let objectType = (this as SqlImportModel).belongsTo('objectType').value() as ObjectTypeModel;

    return objectType?.name != null ? objectType.name : SyncTarget.Objects;
  }

  get syncTarget() {
    if ((this as SqlImportModel).belongsTo('objectType').id() == null) {
      return SyncTarget.People;
    }

    return this.objectTypeName;
  }

  start = modelApiAction({
    path: 'start',
    type: 'POST',
    after(payload) {
      this.store.pushPayload(payload);
    },
  });

  stop = modelApiAction({
    path: 'stop',
    type: 'POST',
    after(payload) {
      this.store.pushPayload(payload);
    },
  });

  updateQuery = modelApiAction({
    path: 'update_query',
    type: 'POST',
    after(payload) {
      this.store.pushPayload(payload);
    },
  });
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'sql-import': SqlImportModel;
  }
}
